import React, { useMemo, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  CellLeftV8,
  order2Sorting,
  toastSuccess,
  toastFailure,
  promiseModalDialog,
  TableDataState,
  EditCellV8,
  TableCellV8,
  TablePaginatedV8,
  CellYesNoV8,
  CellYesNoEngV8,
  CellYesNoReverseColorsV8,
} from 'motion-components';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  isActiveColumn,
  lastDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
} from 'shared-pages/common/grid-helpfunc';
import { useMutation } from '@apollo/client';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import * as Lo from 'lodash';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    dataCount,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateMaterial] = useMutation(gqlUpdate);
  const [addNewMaterial] = useMutation(gqlAdd);
  const [deleteMaterial] = useMutation(gqlDelete);

  const columns = useMemo(
    () => [
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellLeftV8,
        size: 5,
      },
      {
        id: 'name',
        header: t('Απόβλητο'),
        columnName: t('Απόβλητο'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 50,
      },
      {
        id: 'ekaCodeValues',
        header: t('Σετ Κωδικών ΕΚΑ (*)'),
        columnName: t('Σετ Κωδικών ΕΚΑ'),
        accessorKey: 'ekaCodeValues',
        orderByField: 'ekaCodeValues',
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 50,
      },
      {
        id: 'defEkaCode',
        header: t('Default Κωδικός ΕΚΑ'),
        columnName: t('Default Κωδικός ΕΚΑ'),
        accessorKey: 'defEkaCode',
        orderByField: 'defEkaCode',
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 50,
      },
      {
        id: 'unClassValues',
        header: t('Σετ Κλάσεων UN (*)'),
        columnName: t('Σετ Κλάσεων UN'),
        accessorKey: 'unClassValues',
        orderByField: 'unClassValues',
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 50,
      },
      {
        id: 'defUnClass',
        header: t('Default Κλάση UN'),
        columnName: t('Default Κλάση UN'),
        accessorKey: 'defUnClass',
        orderByField: 'defUnClass',
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 20,
      },
      {
        id: 'isHazardous',
        header: t('Είναι Επικίνδυνα'),
        columnName: t('Είναι Επικίνδυνα'),
        accessorKey: 'isHazardous',
        orderByField: 'isHazardous',
        cell: TableCellV8,
        meta: {
          onLabel: t('ΝΑΙ'),
          offLabel: t('ΟΧΙ'),
          type: 'checkbox',
          viewCell: i18n.language === 'gr' ? CellYesNoV8 : CellYesNoEngV8,
        },
        size: 20,
      },
      {
        id: 'useInProduction',
        header: t('Eίναι Παραγόμενο'),
        columnName: t('Eίναι Παραγόμενο'),
        accessorKey: 'useInProduction',
        orderByField: 'useInProduction',
        cell: TableCellV8,
        meta: {
          onLabel: t('ΝΑΙ'),
          offLabel: t('ΟΧΙ'),
          type: 'checkbox',
          viewCell: i18n.language === 'gr' ? CellYesNoV8 : CellYesNoEngV8,
        },
        size: 20,
      },
      {
        id: 'notes',
        header: t('Παρατηρήσεις'),
        columnName: t('Παρατηρήσεις'),
        accessorKey: 'notes',
        orderByField: 'notes',
        size: 80,
        cell: TableCellV8,
        meta: { type: 'text' },
      },
      isActiveColumn(t, i18n),
      firstDateUserColumn(t),
      lastDateUserColumn(t),
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        size: 120,
        cell: ({ row, table }) => {
          return true ? EditCellV8({ row, table }) : <>-</>;
        },
      },
    ],
    [t],
  );

  const tableRef = useRef(null);

  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort, columnDef, orderBy) => {
            onOrderChange(orderBy);
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Deliveries - OnSaveRow row', row, changes);
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              const res = await addNewMaterial({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addMaterial.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateMaterial({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε το Απόβλητο <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) return;
            const res = await deleteMaterial({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) return false; // some error returned
            refetch();
            toastSuccess('Διαγράφηκε το Απόβλητο');
            return true;
          }}
          onNewRow={() => {
            return {
              id: -1,
              isActive: true,
            };
          }}
          topBorderRadius={false}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
